import { useForm, useStore } from '@tanstack/react-form'
import isEqual from 'lodash/isEqual'
import { FF, SearchInput } from 'ui/components'
import { BankAccountSelect, CompanySelect, ContactSelect, LicenseSelect, Select } from 'ui/components/Select'
import { FilterPanel } from 'ui/components/Table/FilterPanel'
import { FilterWrapper } from 'ui/temp/Filter'
import { TableFilterRow } from 'ui/temp/TableFilterRow'
import { DocumentListFilterParams, documentListFilterParamsSchema } from '~bank-documents/types/documentList'

import { useDocumentsListContext } from '../../context/DocumentsListContext'
import { filterIdsBySubjectType } from './utils/filterIdsBySubjectType'

import { s } from './styles'

export const Filter = () => {
  const { data, search, onFilterChange, clear } = useDocumentsListContext()
  const { alertsOptions, subjectTypeOptions, frequencyOptions, internalOptions } = data

  const formApi = useForm({
    defaultValues: {
      ...data.form,
      companyId: data?.form?.onlyMyCompanies ? 'my' : data?.form?.companyId
    }
  })

  const clearForm = () => {
    formApi.reset()
    clear()
  }

  const updateSearchParams = (newValue: Partial<DocumentListFilterParams>) => {
    const subjectType = newValue.subjectType || formApi.getFieldValue('subjectType')
    const filtered = filterIdsBySubjectType<DocumentListFilterParams>({ ...search, ...newValue }, subjectType as string)
    onFilterChange({ ...filtered, page: 1 })
  }

  return (
    <FilterPanel
      isDirty={useStore(formApi.store, (s) => s.isDirty) || !isEqual(documentListFilterParamsSchema.parse({}), search)}
      onReset={clearForm}
      customActions={
        <formApi.Field
          name="search"
          listeners={{
            onChange: ({ value }) => {
              updateSearchParams({ search: value })
            }
          }}
          children={(field) => (
            <FF field={field} input={(inputProps) => <SearchInput {...inputProps} data-qa="Search" />} />
          )}
        />
      }
    >
      <FilterWrapper>
        <TableFilterRow>
          <s.ItemWrapper>
            <formApi.Field
              name="internal"
              listeners={{
                onChange: ({ value }) => {
                  updateSearchParams({ internal: value })
                }
              }}
              children={(field) => (
                <FF
                  field={field}
                  label="Internal"
                  input={(inputProps) => <Select data={internalOptions} {...inputProps} />}
                />
              )}
            />
          </s.ItemWrapper>

          <s.ItemWrapper>
            <formApi.Field
              name="subjectType"
              listeners={{
                onChange: ({ value }) => {
                  updateSearchParams({ subjectType: value })
                }
              }}
              children={(field) => (
                <FF
                  field={field}
                  label="Subject Type"
                  input={(inputProps) => <Select data={subjectTypeOptions} {...inputProps} />}
                />
              )}
            />
          </s.ItemWrapper>

          {data.showBankAccountFilter && (
            <s.ItemWrapper>
              <formApi.Field
                name="bankAccountId"
                listeners={{
                  onChange: ({ value }) => {
                    updateSearchParams({ bankAccountId: value })
                  }
                }}
                children={(field) => (
                  <FF
                    field={field}
                    label="Bank Account"
                    input={(inputProps) => (
                      <BankAccountSelect defaultOption={{ label: 'All', value: null }} {...inputProps} />
                    )}
                  />
                )}
              />
            </s.ItemWrapper>
          )}

          {data.showContactFilter && (
            <s.ItemWrapper>
              <formApi.Field
                name="contactId"
                listeners={{
                  onChange: ({ value }) => {
                    updateSearchParams({ contactId: value })
                  }
                }}
                children={(field) => (
                  <FF
                    field={field}
                    label="Contact"
                    input={(inputProps) => (
                      <ContactSelect defaultOption={{ label: 'All', value: null }} {...inputProps} />
                    )}
                  />
                )}
              />
            </s.ItemWrapper>
          )}

          {data.showLicenseFilter && (
            <s.ItemWrapper>
              <formApi.Field
                name="licenseId"
                listeners={{
                  onChange: ({ value }) => {
                    updateSearchParams({ licenseId: value })
                  }
                }}
                children={(field) => (
                  <FF
                    field={field}
                    label="License"
                    input={(inputProps) => (
                      <LicenseSelect defaultOption={{ label: 'All', value: null }} {...inputProps} />
                    )}
                  />
                )}
              />
            </s.ItemWrapper>
          )}

          <s.ItemWrapper>
            <formApi.Field
              name="companyId"
              listeners={{
                onChange: ({ value }) => {
                  if (typeof value === 'string' && value === 'my') {
                    updateSearchParams({ onlyMyCompanies: true, companyId: undefined })
                  } else {
                    updateSearchParams({ onlyMyCompanies: false, companyId: value })
                  }
                }
              }}
              children={(field) => (
                <FF
                  field={field}
                  label="Relationship"
                  input={(inputProps) => (
                    <CompanySelect
                      hideMyCompaniesFilter={false}
                      defaultOption={{ label: 'All', value: null }}
                      options-data-qa="Relationships"
                      data-qa="Relationships Type"
                      {...inputProps}
                    />
                  )}
                />
              )}
            />
          </s.ItemWrapper>

          <s.ItemWrapper>
            <formApi.Field
              name="frequency"
              listeners={{
                onChange: ({ value }) => {
                  updateSearchParams({ frequency: value })
                }
              }}
              children={(field) => (
                <FF
                  field={field}
                  label="Frequency"
                  input={(inputProps) => <Select data={frequencyOptions} {...inputProps} />}
                />
              )}
            />
          </s.ItemWrapper>
          <s.ItemWrapper>
            <formApi.Field
              name="alerts"
              listeners={{
                onChange: ({ value }) => {
                  updateSearchParams({ alerts: value })
                }
              }}
              children={(field) => (
                <FF
                  field={field}
                  label="Alerts"
                  input={(inputProps) => <Select data={alertsOptions} {...inputProps} />}
                />
              )}
            />
          </s.ItemWrapper>
        </TableFilterRow>
      </FilterWrapper>
    </FilterPanel>
  )
}
